import { useEffect, useState, useCallback, useRef, FC } from "react";
import { Box, Button, Card, CardContent, CardActions, Typography } from "@mui/material";
import { Launch } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { NewLearningRequestStep, WorkflowStageEnum } from "utils/types";
import { useRecommendationsStyles } from "./style";
import LoadingState from "./loading-state";
import { SUBMIT_RECOMMENDATION_DECISION, WORKFLOW_STATE } from "utils/queries";
import {
  WorkflowState,
  WorkflowState_workflowState_workflowState_recommendations,
} from "utils/graphql/WorkflowState";
import { useAppDispatch, RootState } from "store";
import { useSelector } from "react-redux";
import { useLazyQuery, useMutation } from "@apollo/client/react";
import { logger } from "utils/logger";
import { useNavigate } from "react-router-dom";
import { Page } from "utils/constants";
import { RenderHTML } from "components/sanitizeHtml/sanitizeHtml";
import { ConfirmationPopup } from "components";
import { setWorkflowStage } from "store/newLearningRequest";
import { isStageTerminal } from "utils/helpers";
import { SubmitRecommendationDecision } from "utils/graphql/SubmitRecommendationDecision";
import {
  NEW_LEARNING_ACTION_TYPE,
  NEW_LEARNING_ALERT_TYPE,
  showAction,
  LoadingPhase,
} from "../../shared/newLearningHelper";
import useBudgetCheckNavigation from "../../shared/useBudgetCheckNavigation";

interface ProductRecommendationsProps {
  onPrevious: (currentStepParam: NewLearningRequestStep) => void;
  isMobile: boolean;
  onAcceptRecommendation?: () => void;
  closeDialog?: () => void;
  setDialogTitle?: (title: string) => void;
}

export const ProductRecommendations: FC<ProductRecommendationsProps> = ({
  onPrevious,
  isMobile,
  onAcceptRecommendation,
  closeDialog,
  setDialogTitle,
}) => {
  const POLL_INTERVAL_MS = 5000;
  const COMPLETION_TIMEOUT_MS = 600;
  const classes = useRecommendationsStyles({ isMobile });
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const workflowStage = useSelector((state: RootState) => state.newLearningRequest.workflowStage);

  const [loadingPhase, setLoadingPhase] = useState<LoadingPhase>(LoadingPhase.Loading);
  const [progressPercent, setProgressPercent] = useState<number>(10);
  const [acceptedRecommendation, setAcceptedRecommendation] = useState<boolean | null>(null);
  const [showCourseConfirmation, setShowCourseConfirmation] = useState(false);
  const completionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigateWithBudgetCheck = useBudgetCheckNavigation();
  const [loadWorkflowState, { data, error: queryError, stopPolling, startPolling }] =
    useLazyQuery<WorkflowState>(WORKFLOW_STATE, {
      fetchPolicy: "no-cache",
      notifyOnNetworkStatusChange: true,
    });

  const recommendations =
    data?.workflowState?.workflowState?.recommendations?.filter(Boolean) ?? [];
  const recommendationCount = recommendations.length;
  const recommendationStatusMessage =
    recommendationCount === 0
      ? t("no_product_found")
      : t("num_products_selected", {
          count: recommendationCount,
        });

  useEffect(() => {
    if (loadingPhase === LoadingPhase.Ready) {
      setDialogTitle?.(t("your_product_recommendations"));
    } else {
      setDialogTitle?.(t("finding_products"));
    }
  }, [setDialogTitle, t, loadingPhase]);

  const [submitRecommendationDecision] = useMutation<SubmitRecommendationDecision>(
    SUBMIT_RECOMMENDATION_DECISION,
    {
      onCompleted: (data) => {
        if (data.submitRecommendationDecision.success && acceptedRecommendation === false) {
          navigateWithBudgetCheck(onPrevious, NewLearningRequestStep.NlrAdditionalDetails);
        } else if (!data.submitRecommendationDecision.success) {
          logger.error("Failed to submit recommendation decision for data", {
            error: data,
          });
        }
      },
      onError: (error) => {
        const { graphQLErrors, networkError } = error as any;
        logger.error("Failed to submit recommendation decision", {
          error: { graphQLErrors, networkError },
        });
        dispatch(setWorkflowStage(WorkflowStageEnum.Failed));

        showAction(dispatch, NEW_LEARNING_ACTION_TYPE.ALERT_SHOW, {
          type: NEW_LEARNING_ALERT_TYPE.ERROR,
          title: t("product_recommendations"),
          message: t("failed_to_submit_decision"),
        });
      },
    },
  );

  const stopPollingAndClear = useCallback(() => {
    try {
      stopPolling?.();
    } finally {
      setLoadingPhase(LoadingPhase.Ready);
    }
  }, [stopPolling]);

  useEffect(() => {
    if (!data) return;
    const stage = (data?.workflowState?.workflowState?.workflowStage ??
      null) as WorkflowStageEnum | null;
    dispatch(setWorkflowStage(stage));

    if (!isStageTerminal(stage) && stage !== WorkflowStageEnum.AwaitingUserAcceptance) {
      setProgressPercent((prev) => Math.min(90, prev + 12));
    }

    if (stage === WorkflowStageEnum.AwaitingUserAcceptance) {
      setProgressPercent(100);
      setLoadingPhase(LoadingPhase.Complete);
      stopPolling?.();
      completionTimeoutRef.current = globalThis.setTimeout(
        () => setLoadingPhase(LoadingPhase.Ready),
        COMPLETION_TIMEOUT_MS,
      );
      return;
    }
    if (isStageTerminal(stage)) {
      setLoadingPhase(LoadingPhase.Error);
      stopPollingAndClear();
      showAction(dispatch, NEW_LEARNING_ACTION_TYPE.ALERT_SHOW, {
        type: NEW_LEARNING_ALERT_TYPE.ERROR,
        title: t("product_recommendations"),
        message: t("failed_to_load_recommendations"),
      });
    }
  }, [data, dispatch, stopPolling, stopPollingAndClear, t]);

  useEffect(() => {
    if (!queryError) return;
    logger.error("Failed to load recommendations data", {
      error: queryError instanceof Error ? queryError.message : String(queryError),
    });
    setLoadingPhase(LoadingPhase.Error);
    dispatch(setWorkflowStage(WorkflowStageEnum.Failed));

    showAction(dispatch, NEW_LEARNING_ACTION_TYPE.ALERT_SHOW, {
      type: NEW_LEARNING_ALERT_TYPE.ERROR,
      title: t("product_recommendations"),
      message: t("failed_to_load_recommendations"),
    });

    stopPollingAndClear();
  }, [queryError, dispatch, stopPollingAndClear, t]);

  useEffect(() => {
    setProgressPercent(10);
    setLoadingPhase(LoadingPhase.Loading);
    loadWorkflowState();
    startPolling(POLL_INTERVAL_MS);

    return () => {
      if (completionTimeoutRef.current) {
        clearTimeout(completionTimeoutRef.current);
      }
      stopPollingAndClear();
    };
  }, [loadWorkflowState, startPolling, stopPollingAndClear]);

  const handleNoneOfTheseClick = () => {
    if (!isStageTerminal(workflowStage)) {
      setAcceptedRecommendation(false);
      submitRecommendationDecision({
        variables: {
          payload: {
            accepted: false,
          },
        },
      });
    }
  };

  const handleBookProductClick = (
    rec: WorkflowState_workflowState_workflowState_recommendations,
  ) => {
    setShowCourseConfirmation(true);
    const url = `/${Page.Course}/${rec.programmeId}`;
    window.open(encodeURI(url), "_blank", "noopener,noreferrer");
  };

  const handleConfirmCourseSelection = () => {
    setShowCourseConfirmation(false);

    // submit acceptance of this recommendation if not already accepted, then open the product link in a new tab
    if (!acceptedRecommendation) {
      setAcceptedRecommendation(true);
      onAcceptRecommendation?.();
      submitRecommendationDecision({
        variables: {
          payload: {
            accepted: true,
          },
        },
      });
    }

    // Close the main dialog
    closeDialog?.();
  };

  if (loadingPhase === LoadingPhase.Ready) {
    return (
      <Box className={classes.container}>
        <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
          {recommendationStatusMessage}
        </div>
        <section aria-labelledby="recommendations-heading">
          <Box className={classes.header}>{t("product_recommendations")}</Box>
          {recommendationCount === 0 ? (
            <Typography className={classes.emptyState} role="status" aria-live="polite">
              {t("no_product_found")}
            </Typography>
          ) : (
            <Box className={classes.headerDescription}>
              {t("num_products_selected", { count: recommendationCount })}
            </Box>
          )}
          <div className={classes.cardsList}>
            {recommendations.map(
              (rec: WorkflowState_workflowState_workflowState_recommendations | null) =>
                rec ? (
                  <Card key={rec.id} className={classes.recommendationCard}>
                    <CardContent className={classes.cardContent}>
                      <div className={classes.contentBox}>
                        <Typography
                          variant="subtitle1"
                          component="h3"
                          className={classes.title}
                          id={`recommendation-${rec.id}-title`}
                        >
                          {rec.title}
                        </Typography>

                        <Typography
                          variant="body2"
                          color="textSecondary"
                          className={classes.description}
                          id={`recommendation-${rec.id}-description`}
                        >
                          <RenderHTML content={rec.description} />
                        </Typography>
                      </div>
                    </CardContent>
                    <CardActions className={classes.cardActions}>
                      <Button
                        id={`view-product-${rec.id}-btn`}
                        size="small"
                        variant="contained"
                        className={classes.primaryButton}
                        endIcon={<Launch aria-hidden />}
                        aria-label={t("view_this_product")}
                        onClick={() => handleBookProductClick(rec)}
                      >
                        {t("view_this_product")}
                      </Button>
                    </CardActions>
                  </Card>
                ) : null,
            )}
          </div>
        </section>
        <Box className={classes.buttonGroup}>
          {acceptedRecommendation !== true && (
            <Button
              id="product-recommendations-none-of-these-btn"
              type="button"
              variant="outlined"
              onClick={handleNoneOfTheseClick}
              className={classes.primaryButton}
              aria-label={t("none_of_these")}
            >
              {t("none_of_these")}
            </Button>
          )}
        </Box>
        <ConfirmationPopup
          open={showCourseConfirmation}
          title={t("course_selection_confirmation")}
          message=""
          confirmLabel={t("course_selection_confirmation_yes")}
          cancelLabel={t("course_selection_confirmation_no")}
          handleConfirmed={handleConfirmCourseSelection}
          handleClose={() => setShowCourseConfirmation(false)}
        />
      </Box>
    );
  }

  return (
    <LoadingState
      classes={classes}
      t={t}
      progress={loadingPhase}
      progressPercent={progressPercent}
      titleText={t("analysing_your_needs")}
      statusText={t("ai_searching_products")}
    />
  );
};
